import {
  CompactSelection,
  EditableGridCell,
  GridSelection,
  Item,
} from "@glideapps/glide-data-grid";
import { getErrorMessage } from "@/core/api/errors";
import { ApiError } from "@/core/api/types";
import { AI_SPREADSHEET_VALUE_TYPE } from "@/payroll/ai-script/types";
import type {
  IAiScriptEditedCellSelection,
  IAiSpreadsheetCell,
} from "@/payroll/ai-script/types";
import { isAiScriptWorkbookAuthorizationError } from "@/payroll/ai-script/utils";
import { getSuggestedValueCellValue } from "./suggested-value-cell";

export const SHEET_PREFETCH_DWELL_MS = 250;

export const emptySelection: GridSelection = {
  columns: CompactSelection.empty(),
  rows: CompactSelection.empty(),
};

export const formatColumnLetter = (colIndex: number) => {
  let columnNumber = colIndex + 1;
  let columnTitle = "";

  while (columnNumber > 0) {
    const remainder = (columnNumber - 1) % 26;
    columnTitle = String.fromCharCode(65 + remainder) + columnTitle;
    columnNumber = Math.floor((columnNumber - 1) / 26);
  }

  return columnTitle;
};

export const formatCoordinate = (cell: Item) =>
  `${formatColumnLetter(cell[0])}${cell[1] + 1}`;

export const getEditedCellSelection = (
  cell: IAiSpreadsheetCell | undefined,
): IAiScriptEditedCellSelection | null =>
  cell?.override
    ? {
        override: cell.override,
        coordinate: formatCoordinate([cell.col - 1, cell.row - 1]),
      }
    : null;

export const getErrorTitle = (error: unknown) => {
  if (error instanceof ApiError && error.status === 404) {
    return getErrorMessage(error);
  }

  if (error instanceof ApiError && error.status === 401) {
    return "Session expired";
  }

  if (isAiScriptWorkbookAuthorizationError(error)) {
    return "Grid access changed";
  }

  return "Unable to load the Grid";
};

export const getCellErrorCode = (cell: IAiSpreadsheetCell | undefined) => {
  if (!cell?.error) return null;
  if (typeof cell.error === "string") return cell.error;

  return cell.error.code ?? cell.error.message ?? null;
};

export const getFormulaBarValue = (cell: IAiSpreadsheetCell | undefined) => {
  if (!cell) return "";
  if (cell.raw_input != null) return cell.raw_input;
  if (cell.computed_value != null) return String(cell.computed_value);

  return "";
};

export const getEditedRawInput = (newValue: EditableGridCell) => {
  const value = (newValue as { data?: unknown }).data;
  const suggestedValue = getSuggestedValueCellValue(value);

  return suggestedValue ?? String(value ?? "");
};

// Glide renders only the first line in non-wrapped cells. Collapse embedded
// line breaks so imported text remains visible without changing wrapped cells.
export const normalizeSingleLineCellText = (
  value: string,
  cell: IAiSpreadsheetCell,
) =>
  cell.value_type === AI_SPREADSHEET_VALUE_TYPE.text &&
  cell.format?.alignment?.wrap_text !== true
    ? value.replace(/\s*[\r\n]+\s*/g, " ")
    : value;

export const columnNumberFromName = (value: string): number | null => {
  const letters = value.replace(/\$/g, "").trim().toUpperCase();
  if (!/^[A-Z]{1,3}$/.test(letters)) return null;

  let column = 0;
  for (const letter of letters) {
    column = column * 26 + (letter.charCodeAt(0) - 64);
  }
  return column >= 1 && column <= 16384 ? column : null;
};
